import { usersId, type UsersId } from "./Users";
import { projectsId, type ProjectsId } from "./Projects";
import { cliAuthSessionStatus, type default as CliAuthSessionStatus } from "./CliAuthSessionStatus";
import { organizationsId, type OrganizationsId } from "./Organizations";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for cli_auth_sessions */
export type CliAuthSessionsId = string & { __flavor: "CliAuthSessionsId" };

/**
 * Represents the table public.cli_auth_sessions
 * @uniqueConstraints {"cli_auth_sessions_pkey":{"name":"cli_auth_sessions_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]}}
 */
export default interface CliAuthSessionsTable {
  id: ColumnType<CliAuthSessionsId, CliAuthSessionsId | undefined, CliAuthSessionsId>;

  userId: ColumnType<UsersId | null, UsersId | null, UsersId | null>;

  projectId: ColumnType<ProjectsId | null, ProjectsId | null, ProjectsId | null>;

  token: ColumnType<string, string, string>;

  status: ColumnType<CliAuthSessionStatus, CliAuthSessionStatus | undefined, CliAuthSessionStatus>;

  expiresAt: ColumnType<Date, Date | string, Date | string>;

  completedAt: ColumnType<Date | null, Date | string | null, Date | string | null>;

  metadata: ColumnType<unknown | null, unknown | null, unknown | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>;

  organizationId: ColumnType<
    OrganizationsId | null,
    OrganizationsId | null,
    OrganizationsId | null
  >;
}

export type CliAuthSessions = Selectable<CliAuthSessionsTable>;

export type NewCliAuthSessions = Insertable<CliAuthSessionsTable>;

export type CliAuthSessionsUpdate = Updateable<CliAuthSessionsTable>;

export const cliAuthSessionsId = z.string() as unknown as z.Schema<CliAuthSessionsId>;

export const cliAuthSessions = z.object({
  id: cliAuthSessionsId,
  userId: usersId.nullable(),
  projectId: projectsId.nullable(),
  token: z.string(),
  status: cliAuthSessionStatus,
  expiresAt: z.date(),
  completedAt: z.date().nullable(),
  metadata: z.unknown().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  organizationId: organizationsId.nullable(),
});

export const cliAuthSessionsInitializer = z.object({
  id: cliAuthSessionsId.optional(),
  userId: usersId.optional().nullable(),
  projectId: projectsId.optional().nullable(),
  token: z.string(),
  status: cliAuthSessionStatus.optional(),
  expiresAt: z.date(),
  completedAt: z.date().optional().nullable(),
  metadata: z.unknown().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  organizationId: organizationsId.optional().nullable(),
});

export const cliAuthSessionsMutator = z.object({
  id: cliAuthSessionsId.optional(),
  userId: usersId.optional().nullable(),
  projectId: projectsId.optional().nullable(),
  token: z.string().optional(),
  status: cliAuthSessionStatus.optional(),
  expiresAt: z.date().optional(),
  completedAt: z.date().optional().nullable(),
  metadata: z.unknown().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  organizationId: organizationsId.optional().nullable(),
});

// ── constraints for CliAuthSessions ─────────────────────────────────
export const CliAuthSessionsConstraints = {
  cli_auth_sessions_pkey: {
    name: "cli_auth_sessions_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
} as const;
export type CliAuthSessionsConstraintsType = typeof CliAuthSessionsConstraints;
